import * as React from 'react';
import {FC, useMemo} from 'react';
import * as moment from 'moment';
import {useContext} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {
  GridActionsCellItem,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowParams,
  GridToolbarContainer
} from '@mui/x-data-grid';
import StatusDot from '../../../shared/StatusDot';
import {calculateTransitionState, handlePrintAllDocuments, transitionStateToDotStatus} from '../WaveShared';
import {BatchProgressSummaries} from '../WaveBatchInterfaces';
import {TransitionStateDisplay} from '../../../shared/constants';
import {FlexeDataGrid} from '../../../shared/mui/flexe-data-grid/FlexeDataGrid';
import {flexeMuiTheme} from '../../../shared/mui/default-mui-theme';
import {WaveDetailsContext} from './WaveDetailsContext';

// eslint-disable-next-line @typescript-eslint/ban-types, no-empty-pattern
const ContentsBatchesTable: FC<{}> = ({}) => {
  const {isLoading, batches, batchesCount, batchesPaginationModel, setBatchesPaginationModel} = useContext(
    WaveDetailsContext
  );

  const dateFormat = (date: string): string => {
    return moment(date).format('hh:mm A');
  };

  const getPickTime = (batchSummary: BatchProgressSummaries): string => {
    return batchSummary.taskProgressSummary.startedAt
      ? `Started at ${dateFormat(batchSummary.taskProgressSummary.startedAt)}`
      : '--';
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Batch ID',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: ({value}) => <a href={`/wh/batches/${value}`}>{value}</a>
      },
      {
        field: 'status',
        headerName: 'Status',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: ({value}) => (
          <>
            <StatusDot status={transitionStateToDotStatus(value)} />
            {TransitionStateDisplay.get(value)}
          </>
        )
      },
      {
        field: 'picker',
        headerName: 'Picker',
        type: 'string',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: ({value}) => (
          <div className="info-pair">
            <span className="title">{value.currentPicker}</span>
            <span className="light-text">{value.pickTime}</span>
          </div>
        )
      },
      {
        field: 'expected',
        headerName: 'Expected',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: ({value}) => (
          <div className="info-pair">
            <span className="title">{value.totalEaCount} ea</span>
            <span className="light-text underline">{value.totalSkuCount} SKUs</span>
          </div>
        )
      },
      {
        field: 'picked',
        headerName: 'Picked',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: ({value}) => (
          <div className="info-pair">
            <span className="title">{value.pickedEaCount} ea</span>
            <span className="light-text underline">{value.pickedSkuCount} SKUs</span>
          </div>
        )
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            showInMenu={true}
            onClick={() => handlePrintAllDocuments(params.row.documents.reverse().map((doc) => doc.id))}
            label={'Print Batch'}
          />
        ]
      }
    ],
    []
  );

  const rows = useMemo(
    () =>
      batches.map((batch) => ({
        id: batch.batch.id,
        status: calculateTransitionState(batch.taskProgressSummary.status),
        picker: {currentPicker: batch.taskProgressSummary.currentPicker, pickTime: getPickTime(batch)},
        expected: {
          totalEaCount: batch.taskProgressSummary.totalEaCount,
          totalSkuCount: batch.taskProgressSummary.totalSkuCount
        },
        picked: {
          pickedEaCount: batch.taskProgressSummary.pickedEaCount,
          pickedSkuCount: batch.taskProgressSummary.pickedSkuCount
        },
        documents: batch.documents
      })),
    [batches]
  );

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    return {
      id: !isLoading,
      status: !isLoading,
      picker: !isLoading,
      expected: !isLoading,
      picked: !isLoading
    };
  }, [isLoading]);

  const handlePageChange = (newPaginationModel: GridPaginationModel) => {
    setBatchesPaginationModel(newPaginationModel);
  };

  return (
    <div className="contents-table">
      <ThemeProvider theme={flexeMuiTheme}>
        <FlexeDataGrid
          autoHeight
          columns={columns}
          rows={rows}
          rowCount={batchesCount}
          loading={isLoading}
          filterMode={'server'}
          pageSizeOptions={[50]} // fixed at 50, since BatchWaving service does not support page size
          paginationMode={'server'}
          paginationModel={batchesPaginationModel}
          onPaginationModelChange={handlePageChange}
          columnVisibilityModel={columnVisibilityModel}
          getRowHeight={() => 'auto'}
          sx={{
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {py: '8px'}
          }}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          slots={{toolbar: GridToolbarContainer}}
        />
      </ThemeProvider>
    </div>
  );
};

export default ContentsBatchesTable;
